import { FileType } from './types';

export const DEFAULT_FILE_PREFIX = '/document';
export const DOCUMENTATION_FILE_PREFIX = '/documentation';

export function removeTrailingSlash(value: string): string {
  return value.replace(/\/$/, '');
}

/** True when `segment` appears as full path segments, not as a substring of another id. */
export function pathnameIncludesSegment(
  pathname: string,
  segment: string
): boolean {
  const normalizedPath = removeTrailingSlash(pathname);
  const normalizedSegment = removeTrailingSlash(segment);
  if (!normalizedSegment) {
    return false;
  }

  const pathParts = normalizedPath.split('/').filter(Boolean);
  const segmentParts = normalizedSegment.split('/').filter(Boolean);
  if (segmentParts.length === 0) {
    return false;
  }

  for (let i = 0; i <= pathParts.length - segmentParts.length; i += 1) {
    let matches = true;
    for (let j = 0; j < segmentParts.length; j += 1) {
      if (pathParts[i + j] !== segmentParts[j]) {
        matches = false;
        break;
      }
    }
    if (matches) {
      return true;
    }
  }

  return false;
}

/** True when pathname is on, under, or an ancestor of the uploader base path. */
export function isPathRelatedToUploaderBase(
  pathname: string,
  basePath: string
): boolean {
  const path = removeTrailingSlash(pathname);
  const base = removeTrailingSlash(basePath);
  if (!path || !base) {
    return false;
  }

  return (
    path === base || path.startsWith(`${base}/`) || base.startsWith(`${path}/`)
  );
}

export function resolveFilePrefix(prefix?: string): string {
  return prefix ?? DEFAULT_FILE_PREFIX;
}

export function resolveAlternateFilePrefix(prefix: string): string {
  return prefix === DOCUMENTATION_FILE_PREFIX
    ? DEFAULT_FILE_PREFIX
    : DOCUMENTATION_FILE_PREFIX;
}

export function isSamePath(left: string, right: string): boolean {
  return removeTrailingSlash(left) === removeTrailingSlash(right);
}

export function buildUploaderTargetPath(
  basePath: string,
  filePrefix: string,
  fileId: string
): string {
  return `${basePath}${filePrefix}/${fileId}`;
}

export function createDocumentPath(
  baseUrl: string,
  prefix: string,
  documentId?: string
): string {
  return removeTrailingSlash(`${baseUrl}${prefix}/${documentId ?? ''}`);
}

export function isPathInUploaderScope(
  pathname: string,
  baseUrl: string,
  prefix?: string
): boolean {
  const normalizedPathname = removeTrailingSlash(pathname);
  const normalizedBase = removeTrailingSlash(baseUrl);
  const resolvedPrefix = resolveFilePrefix(prefix);
  const scopePrefix = removeTrailingSlash(`${normalizedBase}${resolvedPrefix}`);

  return (
    normalizedPathname === scopePrefix ||
    normalizedPathname.startsWith(`${scopePrefix}/`)
  );
}

export function extractRouteDocumentId(
  pathname: string,
  prefix = DEFAULT_FILE_PREFIX
): string | undefined {
  const marker = `${prefix}/`;
  const markerIndex = pathname.lastIndexOf(marker);
  if (markerIndex === -1) {
    return undefined;
  }

  const documentId = pathname.slice(markerIndex + marker.length).split('/')[0];
  return documentId || undefined;
}

export function canAutoOpenFromCurrentPath(
  pathname: string,
  baseUrl: string,
  files: FileType[],
  options: {
    filePrefix?: string;
    matchedId?: string;
    autoOpenCheck?: (
      baseUrl: string,
      file: FileType,
      prefix?: string
    ) => boolean;
  }
): boolean {
  const normalizedPathname = removeTrailingSlash(pathname);
  const normalizedBase = removeTrailingSlash(baseUrl);
  if (normalizedPathname === normalizedBase) {
    return true;
  }

  if (isPathInUploaderScope(pathname, baseUrl, options.filePrefix)) {
    return true;
  }

  const resolvedPrefix = resolveFilePrefix(options.filePrefix);
  const routeDocumentId =
    options.matchedId ?? extractRouteDocumentId(pathname, resolvedPrefix);

  if (routeDocumentId && options.autoOpenCheck) {
    return options.autoOpenCheck(
      baseUrl,
      { _id: routeDocumentId } as FileType,
      options.filePrefix
    );
  }

  if (
    routeDocumentId &&
    !options.autoOpenCheck &&
    pathname.includes(`${resolvedPrefix}/${routeDocumentId}`)
  ) {
    return true;
  }

  if (!options.autoOpenCheck) {
    return false;
  }

  return files.some((file) =>
    options.autoOpenCheck!(baseUrl, file, options.filePrefix)
  );
}

export function getRouteFileId(
  file: FileType,
  prefix?: string
): string | undefined {
  if (prefix === DOCUMENTATION_FILE_PREFIX) {
    return file?.versionId ?? file?.documentationId ?? file?._id;
  }

  return file?.versionId ?? file?._id;
}

export function matchesUploaderFileToRoute(
  file: FileType,
  matchedId: string | undefined,
  baseUrl: string,
  options?: {
    filePrefix?: string;
    autoOpenCheck?: (
      baseUrl: string,
      file: FileType,
      prefix?: string
    ) => boolean;
  }
): boolean {
  if (options?.autoOpenCheck?.(baseUrl, file, options.filePrefix)) {
    return true;
  }

  if (!matchedId) {
    return false;
  }

  return (
    (!!file?.documentationId && matchedId === file.documentationId) ||
    (!!file?._id && matchedId === file._id) ||
    (!!file?.versionId && matchedId === file.versionId)
  );
}
